import { useEffect, useState, useRef, Suspense, type ReactNode } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import {
  LayoutDashboard, Building2, Users, Settings, LayoutGrid, DollarSign, Activity, CreditCard,
  Receipt, FileSpreadsheet, BarChart3, Landmark, Megaphone, KeyRound, LifeBuoy, Mail, Newspaper,
  PieChart, Bot, ScrollText, Package, Wallet, ShieldCheck, Shield, LogOut, Coins,
  Moon, Sun, Gauge, type LucideIcon,
} from "lucide-react";
import { useTheme } from "next-themes";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import { DelayedSpinner } from "@/components/DelayedSpinner";
import { RouteHold } from "@/components/RouteHold";
import { MOTION, prefersReducedMotion } from "@/lib/motion";
import { AppTopBar } from "@/components/app-shell/AppTopBar";
import { NavGroup, NavSection, RailLink } from "@/components/app-shell/RailNav";
import { AppMain, AppMobileBar, AppSidebar } from "@/components/app-shell/AppShell";
import { useRailCollapsed } from "@/hooks/useRailCollapsed";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useSupportAssignedUnread, useSupportPendingCount } from "@/hooks/useSupportRealtime";
import { APP_HOME_PATH } from "@/lib/copilot-home";

type RailItem = { id: string; label: string; icon: LucideIcon; path: string; brandOnly?: "flow" };
type RailSection = { label: string; items: RailItem[] };

const railSections: RailSection[] = [
  {
    label: "Clientes",
    items: [
      { id: "accounts", label: "Cuentas", icon: Building2, path: "/super-admin/accounts" },
      { id: "accounts-by-plan", label: "Cuentas por plan", icon: PieChart, path: "/super-admin/accounts/by-plan" },
      { id: "users", label: "Usuarios", icon: Users, path: "/super-admin/users" },
      { id: "api-keys", label: "Usuarios API", icon: KeyRound, path: "/super-admin/api-keys" },
      { id: "support", label: "Soporte", icon: LifeBuoy, path: "/super-admin/support" },
      { id: "emails", label: "Correos", icon: Mail, path: "/super-admin/emails", brandOnly: "flow" },
      { id: "announcements", label: "Anuncios", icon: Newspaper, path: "/super-admin/announcements" },
    ],
  },
  {
    label: "IA",
    items: [
      { id: "blueprints", label: "Blueprints", icon: Bot, path: "/super-admin/blueprints" },
      { id: "prompts", label: "Prompts de plataforma", icon: ScrollText, path: "/super-admin/prompts" },
    ],
  },
  {
    label: "Monetizar",
    items: [
      { id: "subscriptions", label: "Suscripciones", icon: Receipt, path: "/super-admin/subscriptions", brandOnly: "flow" },
      { id: "plans", label: "Planes", icon: CreditCard, path: "/super-admin/plans", brandOnly: "flow" },
      { id: "extra-packs", label: "Paquetes extra", icon: Package, path: "/super-admin/extra-packs", brandOnly: "flow" },
      { id: "credits", label: "Créditos", icon: Coins, path: "/super-admin/credits", brandOnly: "flow" },
      { id: "mercadopago", label: "MercadoPago", icon: Landmark, path: "/super-admin/mercadopago", brandOnly: "flow" },
      { id: "ads", label: "Flow Ads", icon: Megaphone, path: "/super-admin/ads", brandOnly: "flow" },
    ],
  },
  {
    label: "Plataforma",
    items: [
      { id: "cron-health", label: "Salud de crons", icon: Activity, path: "/super-admin/cron-health" },
      { id: "availability", label: "Disponibilidad", icon: Gauge, path: "/super-admin/availability" },
      { id: "load-tests", label: "Pruebas k6", icon: Activity, path: "/super-admin/load-tests", brandOnly: "flow" },
      { id: "encryption", label: "Cifrado", icon: ShieldCheck, path: "/super-admin/encryption" },
    ],
  },
];

const financialsItems = [
  { id: "costs", label: "Costos", icon: Receipt, path: "/super-admin/financials/costs" },
  { id: "revenue", label: "Ingresos", icon: DollarSign, path: "/super-admin/financials/revenue" },
  { id: "pnl", label: "P&L", icon: FileSpreadsheet, path: "/super-admin/financials/pnl" },
  { id: "balance", label: "Balance General", icon: BarChart3, path: "/super-admin/financials/balance" },
  { id: "org-balance", label: "Balance por cliente", icon: Wallet, path: "/super-admin/financials/org-balance" },
  { id: "saas", label: "Métricas SaaS", icon: BarChart3, path: "/super-admin/financials/saas" },
  { id: "ai-reconciliation", label: "Reconciliación IA", icon: BarChart3, path: "/super-admin/financials/ai-reconciliation" },
  { id: "coverage", label: "Cobertura de costos", icon: Receipt, path: "/super-admin/financials/coverage" },
];

const settingsItems = [
  { id: "general", label: "General", icon: Settings, path: "/super-admin/settings/general" },
  { id: "messenger", label: "Messenger", icon: Settings, path: "/super-admin/settings/messenger" },
  { id: "instagram", label: "Instagram", icon: Settings, path: "/super-admin/settings/instagram" },
  { id: "whatsapp", label: "WhatsApp", icon: Settings, path: "/super-admin/settings/whatsapp" },
  { id: "whatsapp-embedded", label: "WhatsApp Embedded", icon: Settings, path: "/super-admin/settings/whatsapp-embedded" },
];

export function SuperAdminLayout() {
  const location = useLocation();
  const pendingTickets = useSupportPendingCount();
  const assignedTickets = useSupportAssignedUnread();
  const contentRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    if (prefersReducedMotion() || !contentRef.current) return;
    const animate = () => {
      const root = contentRef.current?.firstElementChild;
      if (!root) return;
      // Page roots often carry Tailwind's transition-* for hover/theme polish —
      // that CSS transition fights gsap writing opacity/transform inline every
      // frame and can freeze the tween mid-flight. Neutralize for the fade,
      // restore on completion so the page's own transitions keep working.
      gsap.set(root, { transition: "none" });
      gsap.from(root, {
        y: MOTION.pageRise, opacity: 0, duration: MOTION.duration, ease: MOTION.ease,
        onComplete() { gsap.set(root, { clearProps: "transition" }); },
      });
    };
    // Outlet content mounts async (Suspense/lazy chunk) — a mutation observer
    // catches the fallback→real swap, since a pathname-only re-run misses it
    // on a hard refresh (chunk resolves after this effect already ran).
    const observer = new MutationObserver(animate);
    observer.observe(contentRef.current, { childList: true });
    animate();
    return () => observer.disconnect();
  }, { scope: contentRef, dependencies: [location.pathname], revertOnUpdate: true });
  const isMobile = useIsMobile();
  const [desktopCollapsed, setDesktopCollapsed] = useRailCollapsed();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Desktop sidebar opens/closes only via the toggle button (no hover-to-expand).
  // On mobile it is a hamburger-triggered off-canvas drawer with full labels.
  const collapsed = isMobile ? false : desktopCollapsed;

  const navigate = useNavigate();
  const { agent, signOut } = useAuth();
  const brand = useBrand();
  const { theme, setTheme } = useTheme();

  const isSettingsActive = location.pathname.startsWith("/super-admin/settings");
  const isFinancialsActive = location.pathname.startsWith("/super-admin/financials");

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Navigate and close the mobile drawer (no-op close on desktop).
  const mobileNav = (to: string) => {
    navigate(to);
    setMobileMenuOpen(false);
  };

  const knownAssigned = useRef<number | null>(null);
  useEffect(() => {
    const previous = knownAssigned.current;
    knownAssigned.current = assignedTickets.length;
    if (previous === null || assignedTickets.length <= previous) return;
    const first = assignedTickets[0];
    toast.message(
      assignedTickets.length === 1
        ? "Te asignaron un ticket de soporte"
        : `Tienes ${assignedTickets.length} tickets asignados sin abrir`,
      {
        description: first?.subject,
        action: {
          label: "Ver",
          onClick: () => navigate(first ? `/super-admin/support?ticket=${first.id}` : "/super-admin/support"),
        },
      },
    );
  }, [assignedTickets, navigate]);

  const initials = agent?.full_name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "SA";

  const supportBadge: ReactNode = (!!pendingTickets || !!assignedTickets.length) && (
    <>
      {!!assignedTickets.length && (
        <span
          title={`${assignedTickets.length} ticket(s) asignados a ti sin abrir`}
          className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground"
        >
        {assignedTickets.length > 99 ? "99+" : assignedTickets.length}
      </span>
    )}
    {!!pendingTickets && !(collapsed && assignedTickets.length) && (
      <span
        title={`${pendingTickets} ticket(s) sin atender`}
        className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-semibold text-destructive-foreground"
      >
        {pendingTickets > 99 ? "99+" : pendingTickets}
      </span>
    )}
  </>
);

const accountMenuItems = (
  <>
    <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setTheme(theme === "dark" ? "light" : "dark"); }}>
      {theme === "dark" ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
      {theme === "dark" ? "Modo claro" : "Modo oscuro"}
    </DropdownMenuItem>
    <DropdownMenuItem onClick={() => mobileNav(APP_HOME_PATH)}>
      <LayoutGrid className="mr-2 h-4 w-4" /> Panel de agente
    </DropdownMenuItem>
    <DropdownMenuSeparator />
    <DropdownMenuItem onClick={signOut} className="text-destructive">
      <LogOut className="mr-2 h-4 w-4" /> Cerrar sesión
    </DropdownMenuItem>
  </>
);

const accountMenu = (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button className="flex max-w-[220px] shrink-0 items-center gap-2.5 rounded-full py-1 pl-1 pr-2.5 text-left transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.06]" aria-label="Cuenta">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground">
          {initials}
        </span>
        <span className="hidden min-w-0 flex-1 md:block">
          <span className="block truncate text-[13px] font-semibold text-ink-900">{agent?.full_name}</span>
          <span className="mt-0.5 block truncate text-[11px] leading-none text-mute">Super Admin</span>
        </span>
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent side="bottom" align="end" className="z-[80] w-64">
      <div className="px-2 py-2">
        <p className="truncate text-xs font-medium text-foreground">{agent?.full_name}</p>
        <p className="truncate text-[11px] text-muted-foreground">Super Admin</p>
      </div>
      <DropdownMenuSeparator />
      {accountMenuItems}
    </DropdownMenuContent>
  </DropdownMenu>
);

return (
  <div className="app-gradient-bg flex h-[100dvh] w-full flex-col overflow-hidden">
    {!isMobile && (
      <AppTopBar
        logoSrc={brand.logos.sidebar}
        iconSrc={brand.logos.icon}
        logoAlt={brand.shortName}
        leadingWidth={collapsed ? "w-14" : "w-[252px]"}
        actions={accountMenu}
        railCollapsed={collapsed}
        onToggleRail={() => setDesktopCollapsed(!desktopCollapsed)}
      />
    )}
    {isMobile && (
      <AppMobileBar
        logoSrc={brand.logos.sidebar}
        logoAlt={brand.shortName}
        onOpenMenu={() => setMobileMenuOpen(true)}
        account={(
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="h-7 w-7 rounded-full bg-primary flex items-center justify-center text-[10px] font-semibold text-primary-foreground">
                {initials}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="bottom" align="end" className="w-56">
              {accountMenuItems}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      />
    )}

    <div className="relative flex min-h-0 flex-1 overflow-hidden">
      <AppSidebar
        isMobile={isMobile}
        collapsed={collapsed}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
        onExpand={() => setDesktopCollapsed(false)}
        logoSrc={brand.logos.sidebar}
        logoAlt={brand.shortName}
        head={(
          <div className={cn(
            "flex w-full min-w-0 items-center gap-2.5 rounded-lg bg-panel shadow-[0_1px_3px_rgb(15_23_42/0.08)]",
            collapsed ? "justify-center p-1.5" : "px-2.5 py-1.5",
          )}>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Shield className="h-4 w-4" />
            </span>
            {!collapsed && (
              <span className="min-w-0 flex-1 text-left">
                <span className="block truncate text-[13px] font-semibold leading-tight text-ink-900">Consola Super Admin</span>
                <span className="block truncate text-[11px] leading-tight text-mute">{brand.shortName}</span>
              </span>
            )}
          </div>
        )}
      >
          <RailLink
            icon={LayoutDashboard}
            label="Dashboard"
            active={location.pathname === "/super-admin"}
            collapsed={collapsed}
            onSelect={() => mobileNav("/super-admin")}
          />
          {railSections.map((section) => {
            const items = section.items.filter((i) => !i.brandOnly || i.brandOnly === brand.id);
            const showFinancials = section.label === "Monetizar" && brand.id === "flow";
            if (!items.length && !showFinancials) return null;
            return (
              <NavSection key={section.label} label={section.label} collapsed={collapsed}>
                {items.map((item) => (
                  <RailLink
                    key={item.id}
                    icon={item.icon}
                    label={item.label}
                    active={location.pathname === item.path}
                    collapsed={collapsed}
                    onSelect={() => mobileNav(item.path)}
                    badge={item.id === "support" ? supportBadge : undefined}
                  />
                ))}
                {showFinancials && (
                  <NavGroup
                    icon={DollarSign}
                    label="Finanzas"
                    active={isFinancialsActive}
                    collapsed={collapsed}
                    sections={[{ id: "financials", items: financialsItems }]}
                    isItemActive={(sub) => location.pathname === sub.path}
                    onSelect={mobileNav}
                  />
                )}
              </NavSection>
            );
          })}
          <NavSection label="Configurar" collapsed={collapsed}>
            <NavGroup
              icon={Settings}
              label="Configuración"
              active={isSettingsActive}
              collapsed={collapsed}
              sections={[{ id: "settings", items: settingsItems }]}
              isItemActive={(sub) => location.pathname === sub.path}
              onSelect={mobileNav}
            />
          </NavSection>
        </AppSidebar>

        <AppMain isMobile={isMobile}>
          <div className={cn("flex min-h-0 flex-1 flex-col overflow-y-auto", isMobile ? "m-2" : "m-3")}>
            <div ref={contentRef} className="contents">
              <RouteHold>
                <Suspense fallback={<DelayedSpinner />}>
                  <Outlet />
                </Suspense>
              </RouteHold>
            </div>
          </div>
        </AppMain>
      </div>
    </div>
  );
}
